export type CsvDelimiter = ',' | ';';

const SUPPORTED: CsvDelimiter[] = [',', ';'];

export const CSV_DELIMITER_LABELS: Record<CsvDelimiter, string> = {
  ',': 'Comma (,)',
  ';': 'Semicolon (;)',
};

/**
 * Picks the delimiter of a CSV from its first line only.
 *
 * The header row is the one line where a delimiter character is unlikely to appear
 * inside a value, so counting there avoids the decimal commas ("353,00") European
 * exports put in every data row — those outvote the real separator when the whole
 * file is counted, which is how SheetJS's own guess goes wrong. Comma wins ties.
 *
 * Must stay in sync with carboncare-backend/src/utils/csvDelimiter.ts: mappings are
 * keyed by column index, so a disagreement silently scrambles them.
 */
export function detectCsvDelimiter(text: string): CsvDelimiter {
  if (!text) return ',';

  const withoutBom = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const newline = withoutBom.indexOf('\n');
  const firstLine = (newline === -1 ? withoutBom : withoutBom.slice(0, newline)).replace(/\r$/, '');

  let inQuotes = false;
  let commas = 0;
  let semicolons = 0;

  for (const char of firstLine) {
    if (char === '"') inQuotes = !inQuotes;
    else if (inQuotes) continue;
    else if (char === ',') commas++;
    else if (char === ';') semicolons++;
  }

  return semicolons > commas ? ';' : ',';
}

/** Narrows a stored or user-supplied delimiter, returning undefined for anything unsupported. */
export function normalizeCsvDelimiter(value: unknown): CsvDelimiter | undefined {
  return SUPPORTED.includes(value as CsvDelimiter) ? (value as CsvDelimiter) : undefined;
}
